import React, { useEffect, useRef, useState } from "react";
import style from "./style.module.scss";
import classnames from "classnames";
import { Icon, jnjCheck } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import Select, { components, StylesConfig } from "react-select";
import { Typography } from "../../atoms/Typography/Typography";
import { getCustomAriaLabels } from "../../atoms/Select/Select.Helpers";
import { useTranslation } from "react-i18next";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";

import {
    FilterSelectOptionType,
    getFilterStyle,
    getStyle,
} from "@jmc/solid-design-system/src/components/atoms/Select/Select.Helpers";
import {
    ComponentsType,
    DropdownIndicator,
    FilterSingleSelectOption,
} from "@jmc/solid-design-system/src/components/atoms/Select/Select";

interface FilterSelectProps {
    id?: string;
    customClass?: string;
    placeholder: string;
    options: FilterSelectOptionType[];
    clickAction: (value: FilterSelectOptionType) => void;
    isMulti?: boolean;
    relativePosition?: boolean;
    invertedSelection?: boolean;
    shouldShowMenuList?: boolean;
    enableClearFilter?: boolean;
}

interface CheckBoxProps {
    name: string;
    value: string;
    checked: boolean;
}

/**
 * TODO: Rebranding: These styles where different than the ones used in Select component.
 * The whole function can be removed and use getStyle so everything is consistent after rebranding.
 */
const singleSelectDesktopStyles = (jnjFullBranded: boolean): StylesConfig<FilterSelectOptionType, false> => {
    return jnjFullBranded
        ? getStyle({ ...getFilterStyle(), jnjFullBranded })
        : {
              control: (provided, state) => ({
                  ...provided,
                  height: "calc(var(--spacing-L) + var(--spacing-M))",
                  boxShadow: "none",
                  borderColor: "var(--grey-100)",
                  outline: state.isFocused ? "5px auto -webkit-focus-ring-color" : "0 !important",
                  "&:hover": {
                      borderColor: "var(--grey-100)",
                  },
              }),
              container: (provided) => ({
                  ...provided,
              }),
              dropdownIndicator: (provided, state) => ({
                  ...provided,
                  color: "var(--text-primary)",
                  transition: "all .2s ease",
                  transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : null,
              }),
              menu: (provided) => ({
                  ...provided,
                  marginTop: 0,
                  zIndex: 2,
              }),
              menuList: (provided) => ({
                  ...provided,
                  padding: "var(--spacing-XS)",
              }),
              option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? "var(--grey-light)" : "white",
                  color: "var(--text-light)",
                  borderRadius: "var(--card-border-radius)",
                  paddingInlineStart: "0",
                  "&:hover": {
                      color: "var(--text-white)",
                      backgroundColor: "var(--primary)",
                      borderRadius: "var(--card-border-radius)",
                  },
              }),
          };
};

const singleSelectMobileStyles = (jnjFullBranded: boolean): StylesConfig<FilterSelectOptionType, false> => {
    return {
        ...singleSelectDesktopStyles(jnjFullBranded),
        menu: (provided) => ({
            ...provided,
            marginTop: 0,
            position: "relative",
        }),
    };
};

/**
 * TODO: Rebranding: These styles where different than the ones used in Select component.
 * The whole function can be removed and use getStyle so everything is consistent after rebranding.
 */
const multiSelectDesktopStyles = (jnjFullBranded: boolean): StylesConfig<FilterSelectOptionType, true> => {
    return jnjFullBranded
        ? getStyle({ ...getFilterStyle(), jnjFullBranded })
        : {
              control: (provided, state) => ({
                  ...provided,
                  height: "calc(var(--spacing-L) + var(--spacing-M))",
                  boxShadow: "none",
                  borderColor: "var(--grey-100)",
                  outline: state.isFocused ? "5px auto -webkit-focus-ring-color" : "0 !important",
                  "&:hover": {
                      borderColor: "var(--grey-100)",
                  },
              }),
              container: (provided) => ({
                  ...provided,
              }),
              option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? "var(--grey-light)" : "transparent",
              }),
              dropdownIndicator: (provided, state) => ({
                  ...provided,
                  color: "var(--text-primary)",
                  transition: "all .2s ease",
                  transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : null,
              }),
              menu: (provided) => ({
                  ...provided,
                  marginTop: 0,
                  zIndex: 2,
                  minWidth: "100%",
                  width: "max-content",
              }),
              placeholder: (provided) => ({
                  ...provided,
                  position: "initial",
                  transform: "initial",
              }),
          };
};

const multiSelectMobileStyles = (jnjFullBranded: boolean): StylesConfig<FilterSelectOptionType, true> => {
    const original = multiSelectDesktopStyles(jnjFullBranded);
    return {
        ...original,
        menu: (provided) => ({
            ...original.menu(provided),
            marginTop: 0,
            top: jnjFullBranded ? "0" : "var(--spacing-minus-S)",
            position: "relative",
            width: "100%",
        }),
    };
};

export const CheckBox = ({ name, value, checked }: CheckBoxProps): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();

    const icon = jnjFullBranded ? (
        <Icon icon={jnjCheck} type="jnj" size="xs" color="inherit" />
    ) : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="14px" height="14px">
            <path d="M0 0h24v24H0z" fill="none" />
            <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
        </svg>
    );
    return (
        <div className={style.checkbox}>
            {checked && (
                <input
                    className={style.checkbox_box}
                    type="checkbox"
                    name={name}
                    disabled={false}
                    data-test-id={`CheckBox.Box.${name}.${value}`}
                    defaultChecked
                />
            )}
            {!checked && (
                <input
                    className={style.checkbox_box}
                    type="checkbox"
                    name={name}
                    disabled={false}
                    data-test-id={`CheckBox.Box.${name}.${value}`}
                />
            )}
            <label htmlFor={name} className={style.checkbox_label} data-test-id={`CheckBox.Label.${name}.${value}`}>
                {checked ? icon : ""}
            </label>
        </div>
    );
};

const FilterMultiSelectOption = (props: any) => {
    const { data } = props;
    const { jnjFullBranded } = useJnjBranding();

    return (
        <div className={classnames(style.filterOption)}>
            <components.Option {...props} tabIndex={0}>
                <div className={classnames(style.optionCheckbox)}>
                    <CheckBox name={data?.label} value={data?.value} checked={data?.isSelected} />
                </div>
                <div className={classnames(style.optionLabel)}>
                    <Typography
                        size={jnjFullBranded ? "inherit" : "m"}
                        weight={jnjFullBranded ? "inherit" : "400"}
                        color={jnjFullBranded ? "inherit" : "light"}
                    >
                        {data?.label}
                    </Typography>
                </div>
            </components.Option>
        </div>
    );
};

export const FilterSelect: React.FunctionComponent<FilterSelectProps> = ({
    id,
    customClass = null,
    placeholder = "test",
    options,
    clickAction,
    isMulti = false,
    relativePosition = false, // TODO: Rebranding: This is only used to switch to mobile styles. Should be removed and done automatically.
    invertedSelection = false, // Use it if the values are stored in a useState and the Aria messages are wrong
    shouldShowMenuList = true,
    enableClearFilter = true,
}: FilterSelectProps): JSX.Element => {
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();
    const selectElementRef = useRef(null);
    const [menuIsOpen, setMenuIsOpen] = useState(false);

    let numSelectedOptions = 0;
    let styles = relativePosition
        ? singleSelectMobileStyles(jnjFullBranded)
        : singleSelectDesktopStyles(jnjFullBranded);
    let onChange = (value: FilterSelectOptionType) => clickAction(value);

    if (isMulti) {
        styles = relativePosition ? multiSelectMobileStyles(jnjFullBranded) : multiSelectDesktopStyles(jnjFullBranded);
        onChange = (
            _value: unknown,
            actionMeta: { action: string; option: FilterSelectOptionType; removedValue: FilterSelectOptionType },
        ) => {
            if (actionMeta.action === "select-option" || actionMeta.action === "deselect-option") {
                clickAction(actionMeta.option);
            } else if (actionMeta.action === "remove-value") {
                clickAction(actionMeta.removedValue);
            }
        };
        options?.forEach((option: FilterSelectOptionType) => {
            if (option.isSelected) {
                numSelectedOptions++;
            }
        });
    }

    const clearFilter = () => {
        options.forEach((option: FilterSelectOptionType) => {
            option.isSelected = false;
        });

        clickAction(options);
        setMenuIsOpen(false);
    };

    // Managing ARIA attributes for select component
    useEffect(() => {
        const inputEle = selectElementRef?.current?.inputRef;
        if (inputEle) {
            inputEle?.setAttribute("aria-autocomplete", "none");
            inputEle?.setAttribute("aria-haspopup", "false");
        }
    }, [selectElementRef]);

    const CustomMenuList = (props: any) => {
        const { children } = props;
        const menuRef = useRef(null);

        const closeMenu = () => {
            setMenuIsOpen(false);
        };

        const isAnyValueSelected = options.some((option) => option.isSelected === true);
        const top = menuRef?.current?.getBoundingClientRect()?.top;
        const screenHeight = typeof window !== "undefined" ? window.innerHeight : 0;

        // Do not allow the menu to be bigger than the screen
        const menuStyles = menuRef?.current
            ? ({
                  maxHeight: screenHeight - top + "px",
                  overflowY: "auto",
              } as React.CSSProperties)
            : {};

        return (
            <div ref={menuRef} style={menuStyles}>
                {children}
                <div className={style.selectActions}>
                    <Typography
                        variant="label-01"
                        weight={"400"}
                        color={enableClearFilter && isAnyValueSelected ? "gray" : "disabled"}
                        onClick={enableClearFilter && isAnyValueSelected ? clearFilter : undefined}
                    >
                        {t("Clear filters", { ns: "common" })}
                    </Typography>
                    <div>
                        <Button
                            variant={"naked"}
                            color={"primary-500"}
                            textVariant={"label-01"}
                            onClick={isAnyValueSelected ? closeMenu : undefined}
                            disabled={!isAnyValueSelected}
                        >
                            {t("Done", { ns: "common" })}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    const components: ComponentsType = {
        Option: isMulti ? FilterMultiSelectOption : FilterSingleSelectOption,
        IndicatorSeparator: (): JSX.Element => null,
    };

    if (jnjFullBranded) {
        components.DropdownIndicator = DropdownIndicator;

        if (shouldShowMenuList) {
            components.MenuList = CustomMenuList;
        }
    }

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
            setMenuIsOpen(!menuIsOpen);
        }
    };

    // for easy debugging add `menuIsOpen={true}`
    return (
        <Select
            id={id}
            //menuIsOpen={true}
            menuIsOpen={menuIsOpen}
            onMenuOpen={() => setMenuIsOpen(true)}
            onMenuClose={() => setMenuIsOpen(false)}
            onKeyDown={handleKeyDown}
            className={customClass ?? ""}
            data-test-id={`Filter.Select.${id}`}
            ref={selectElementRef}
            placeholder={
                <Typography
                    size={jnjFullBranded ? "inherit" : "m"}
                    weight={jnjFullBranded ? "inherit" : "400"}
                    color={jnjFullBranded ? "inherit" : numSelectedOptions === 0 ? "light" : "primary"}
                >
                    {placeholder}
                </Typography>
            }
            aria-label={placeholder}
            ariaLiveMessages={getCustomAriaLabels(t, invertedSelection)}
            options={options}
            components={components}
            isClearable={false}
            controlShouldRenderValue={false}
            styles={styles}
            closeMenuOnSelect={isMulti ? false : true}
            hideSelectedOptions={false}
            isMulti={isMulti}
            onChange={onChange}
            tabSelectsValue={false}
            value={
                isMulti
                    ? options?.filter((option) => option.isSelected)
                    : options?.filter((option) => option.isSelected)?.[0]
            }
        ></Select>
    );
};

export default FilterSelect;
